import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkle, Trash, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIO_COLOR = {
  "Priority 1": "bg-priority-high text-white",
  "Priority 2": "bg-priority-med text-black",
  "Priority 3": "bg-priority-low text-white",
};

export default function TargetsPanel({ project, reload, catalog }) {
  const id = project.id;
  const targets = project.targets || [];
  const [busy, setBusy] = useState(null);

  const interpret = async (tid) => {
    setBusy(tid);
    try { await api.post(`/projects/${id}/targets/${tid}/interpret`); toast.success("AI interpretation generated"); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const patch = async (tid, body) => {
    try { await api.put(`/projects/${id}/targets/${tid}`, body); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (tid) => {
    try { await api.delete(`/projects/${id}/targets/${tid}`); toast.success("Target removed"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (targets.length === 0)
    return <p className="text-sm text-muted-foreground">No targets yet. Run pre-processing to auto-generate ranked exploration targets from the prospectivity model.</p>;

  const profile = (catalog?.commodity_profiles || {})[project.commodity];

  return (
    <div className="space-y-3">
      {profile && (
        <div className="border border-border rounded-md p-3 bg-secondary/30" data-testid="commodity-model">
          <div className="flex items-center justify-between">
            <span className="font-display font-semibold text-sm">{profile.label} exploration model</span>
            <Badge className="bg-primary/15 text-primary text-[9px]">Commodity engine</Badge>
          </div>
          <div className="mt-2 space-y-1 text-[11px] leading-relaxed">
            <div><span className="overline mr-1">Deposit models</span>{profile.deposit_models.join(" · ")}</div>
            <div><span className="overline mr-1">Alteration indicators</span>{profile.alteration_indicators.join(", ")}</div>
            <div><span className="overline mr-1">Structural / host controls</span>{[...profile.structural_controls, ...profile.litho_geomorph_controls].join(", ")}</div>
            <div className="flex flex-wrap gap-1 pt-1">
              {Object.entries(profile.weights).map(([k, v]) => (
                <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted">{k.replace(/_/g, " ")} {v}%</span>
              ))}
            </div>
            <div className="text-muted-foreground pt-1">{profile.ranking_criteria}</div>
          </div>
        </div>
      )}
      <p className="overline">{targets.length} ranked targets · {project.commodity}</p>
      {targets.map((t) => (
        <div key={t.target_id} className="border border-border rounded-md p-3" data-testid={`target-${t.target_id}`}>
          <div className="flex items-center justify-between">
            <div className="font-mono font-semibold text-sm">{t.target_id}</div>
            <Badge className={PRIO_COLOR[t.priority] || ""}>{t.priority}</Badge>
          </div>
          <div className="grid grid-cols-4 gap-1 mt-2 text-center">
            {[["Prosp.", t.prospectivity_score], ["Spectral", t.spectral_score], ["Struct.", t.structural_score], ["Conf.", t.confidence_score]].map(([l, v]) => (
              <div key={l}><div className="font-mono font-semibold text-sm">{v}</div><div className="overline">{l}</div></div>
            ))}
          </div>
          <div className="font-mono text-[10px] text-muted-foreground mt-2">
            {t.centroid?.lat}, {t.centroid?.lon}
          </div>
          {t.rationale && (
            <div className="mt-2 text-[11px] leading-relaxed border-l-2 border-priority-med pl-3 text-muted-foreground" data-testid={`rationale-${t.target_id}`}>
              <span className="overline block mb-0.5">Why this target</span>{t.rationale}
            </div>
          )}
          {t.shap && t.shap.length > 0 && (
            <div className="mt-2" data-testid={`shap-target-${t.target_id}`}>
              <span className="overline block mb-1">Feature drivers (SHAP)</span>
              <div className="flex flex-wrap gap-1">
                {t.shap.map((s) => (
                  <span key={s.feature} className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${s.shap >= 0 ? "bg-priority-high/20 text-priority-high" : "bg-secondary text-muted-foreground"}`}>
                    {s.feature.replace(/_/g, " ")} {s.shap >= 0 ? "+" : ""}{s.shap}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Select value={t.priority} onValueChange={(v) => patch(t.target_id, { priority: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`prio-${t.target_id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{(catalog?.target_priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8" onClick={() => del(t.target_id)} data-testid={`del-${t.target_id}`}><Trash size={14} /></Button>
          </div>
          <Button size="sm" variant="secondary" className="w-full mt-2 h-8" onClick={() => interpret(t.target_id)} disabled={busy === t.target_id} data-testid={`interpret-${t.target_id}`}>
            {busy === t.target_id ? <><CircleNotch size={14} className="mr-1 animate-spin" />Interpreting…</> : <><Sparkle size={14} className="mr-1" weight="fill" />AI Interpretation</>}
          </Button>
          {t.interpretation && (
            <div className="mt-2 text-[11px] leading-relaxed border-l-2 border-primary pl-3 text-muted-foreground whitespace-pre-line" data-testid={`interp-${t.target_id}`}>
              {t.interpretation}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
